/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import React, { useEffect, useState } from "react";

import Label from '../formsUi/common/label/Label';
import FileUpload from '../formsUi/fileUpload/FileUpload';
import ValidationMessage from '../formsUi/common/validation/ValidationMessage';

/**
 * A form field component for handling file uploads with validation and action handling.
 *
 * @param {Object} props - The properties object.
 */
const FormFileUploadControls = ({
    name,
    error,
    onChange,
    formData,
    labelName,
    dataField,
    isRequired,
    formSetting,
    fieldSetting,
    overRideProps,
    onActionChange,
    onFileFleidBlur,
}) => {

    let fileFieldValue = formData?.[dataField];
    const [fileValues, setFileValues] = useState([]);

    const { allowMultipleFiles, acceptedFileFormats } = fieldSetting;

    const handleInputChange = (e) => {
        if (!onChange) return;

        if (e.target.files.length > 0) {
            let newFiles = Array.from(e.target.files);

            if (!allowMultipleFiles) {
                processNewFiles([newFiles[0]]);
            } else {
                processNewFiles(newFiles);
            }

        } else {
            setFileValues([]);
            onChange(dataField, null);
        }
    };

    const processNewFiles = (newFiles) => {
        // Process only the newly added files
        const filePromises = newFiles.map(readFileAsDataURL);

        Promise.all(filePromises).then((fileDataArray) => {
            const newFileObjects = fileDataArray.map((fileData, index) => ({
                file: newFiles[index],
                fileName: newFiles[index].name,
                base64Data: fileData,
            }));

            // Combine with already uploaded files
            const allUploadedFiles = allowMultipleFiles
                ? [...fileValues, ...newFileObjects]
                : newFileObjects;

            // Update states and trigger events
            const filesToPass = allowMultipleFiles ? allUploadedFiles : newFileObjects;
            onChange(dataField, filesToPass);
            handleOnBlur(filesToPass);
            setFileValues(filesToPass); // Update file values
        });
    };

    const handleOnBlur = (files) => {

        if (!files) return;
        const filesArray = Array.isArray(files) ? files : [files];

        onFileFleidBlur?.(dataField, filesArray, fieldSetting);
    };

    const readFileAsDataURL = (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
        });
    };

    const handleActionChange = (action,index) => {
        if (onActionChange) {
            onActionChange(action, dataField, index)
        }
    }

    const handleClearFile = (index) => {
        const newFileValues = [...fileValues];
        newFileValues.splice(index, 1);

        setFileValues(newFileValues);

        onChange(dataField, newFileValues);
        handleActionChange("CLEAR")
    };

    const handleClearAll = () => {
        onChange(dataField, null);
        setFileValues([]);
    }

    const handleActionClick = (index, action) => {
        handleActionChange(action, index);
    };

    useEffect(() => {
        if (fileFieldValue) {
            if (typeof fileFieldValue === 'string') {
                setFileValues([fileFieldValue]);
            }
            else if (Array.isArray(fileFieldValue)) {
                setFileValues(fileFieldValue);
            } else if (fileFieldValue) {
                setFileValues([fileFieldValue])
            }
        }
        else {
            setFileValues([])
        }
    }, [fileFieldValue]);

    useEffect(() => {
        if (error && fileValues.length > 0) {
            handleClearAll();
        }
    }, [error, fileValues]);

    return (
        <>
            <div className="input-label-part">
                {labelName && labelName !== "" && (
                    <Label labelName={labelName} for={name} isRequired={isRequired} />
                )}
                {/* {console.log('fileFieldValue',fileFieldValue)} */}
                
                <FileUpload
                    onClear={handleClearFile}
                    onClearAll={handleClearAll}
                    onChange={handleInputChange}
                    onBlur={handleOnBlur}
                    onActionClick={handleActionClick}
                    selectedFileValues={fileValues}
                    isDisable={fieldSetting?.isDisable || formSetting?.isViewOnly || overRideProps?.isDisable}
                    isMultipleFileAllowed={allowMultipleFiles}
                    isDownloadButton={fieldSetting.isDownloadable}
                    acceptedFileFormats={acceptedFileFormats}
                    placeholder={fieldSetting?.placeholder}
                    isShowCustomPreview={fieldSetting?.isShowCustomPreview}
                    fieldSetting={fieldSetting}
                />
            </div>
            <ValidationMessage error={error || ""}
                helperText={fieldSetting?.helperText}
                successText={overRideProps?.successText ?? fieldSetting?.successText} />
        </>
    );
};

FormFileUploadControls.propTypes = {
    name: PropTypes.string,
    error: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onFileFleidBlur: PropTypes.func,
    labelName: PropTypes.string,
    dataField: PropTypes.string.isRequired,
    isRequired: PropTypes.bool,
    formSetting: PropTypes.shape({
        isViewOnly: PropTypes.bool
    }),
    fieldSetting: PropTypes.shape({
        minFileSizeInMB: PropTypes.number,
        maxFileSizeInMB: PropTypes.number,
        allowMultipleFiles: PropTypes.bool,
        acceptedFileFormats: PropTypes.arrayOf(PropTypes.string),
        isDisable: PropTypes.bool,
        isDownloadable: PropTypes.bool,
        placeholder: PropTypes.string,
    }).isRequired,
    overRideProps: PropTypes.shape({
        isDisable: PropTypes.bool
    }),
    onActionChange: PropTypes.func,
};

export default FormFileUploadControls;